import * as React from "react";
import { CloseCircleFilled } from "@ant-design/icons";
import { useEffectOnce } from "usehooks-ts";
import { usePopupContext } from "src/hooks/usePopup";
import { getParent } from "src/utils/dom";
import { data_focus, InputClause } from "./definitions";
import styles from "./question-input.module.css";
import { useClauseColor } from "./useClauseColors";

export function Clause(
  props: React.PropsWithChildren<{
    clause: InputClause;
    title: string;
    renderMenu(): React.ReactNode;
    onRemove(): void;
    onMenuClose(): void;
    icon?: React.ReactNode;
  }>
) {
  return (
    <div
      data-clausetype={props.clause.type}
      className={[
        styles.clause,
        styles[`clause-${props.clause.type}`],
        props.icon && styles.hasIcon,
        "hover-show",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <ClauseButton
        startOpened={props.clause.status === "init"}
        focusKey={props.clause.key}
        onMenuClose={props.onMenuClose}
        renderMenu={props.renderMenu}
        clause={props.clause}
      >
        <div className={styles.label}>
          {props.icon}
          <div className="trunc">{props.title}</div>
        </div>
        <div style={{ minWidth: 20 }}>{props.children}</div>
        <div>
          <CloseCircleFilled
            title="Remove"
            className="hover-show-target hover-show-target-reserve-space"
            onClick={(e) => {
              e.stopPropagation();
              props.onRemove();
            }}
          />
        </div>
      </ClauseButton>
    </div>
  );
}

// TODO s/30/poll for element
export function ClauseButton(
  props: React.PropsWithChildren<{
    className?: string;
    onMenuClose(): void;
    renderMenu(resize?: () => void): React.ReactNode;
    focusKey?: string;
    startOpened?: boolean;
    clause?: InputClause;
    style?: React.CSSProperties;
  }>
) {
  const [visible, setVisible] = React.useState(false);
  const popup = usePopupContext();
  const propsRef = React.useRef({
    close: props.onMenuClose,
    key: props.focusKey,
  });
  propsRef.current = { close: props.onMenuClose, key: props.focusKey };
  // refocuses on close
  React.useEffect(() => {
    if (!popup.isOpen && visible) {
      setVisible(false);
      propsRef.current.close();
      setTimeout(() => {
        // TODO if it can't find it eventually, it should switch to the add button
        if (propsRef.current.key) {
          (
            document.querySelector(data_focus(propsRef.current.key)) as
              | HTMLInputElement
              | undefined
          )?.focus();
        }
      }, 30);
    }
  }, [visible, popup.isOpen]);
  const openPopup = (
    e:
      | HTMLElement
      | React.MouseEvent<HTMLElement>
      | React.KeyboardEvent<HTMLElement>
  ) => {
    setVisible(true);
    popup.setPopup(e, props.renderMenu(popup.resize));
  };

  // initial open
  useEffectOnce(() => {
    setTimeout(() => {
      if (props.startOpened)
        openPopup(
          (document.querySelector(data_focus(propsRef.current.key)) as
            | HTMLInputElement
            | undefined)!
        );
    }, 30);
  });
  const color = useClauseColor(props.clause);

  const classNames = [styles.button];
  if (props.className) classNames.push(props.className);
  if (visible) classNames.push(styles.hasOpenMenu);
  return (
    <div
      data-focus={props.focusKey}
      style={{
        backgroundColor: `${color}66`,
        borderColor: `${color}`,
        ...props.style,
      }}
      tabIndex={0}
      className={classNames.join(" ")}
      onClick={(e) => {
        // const useTarget = e.clientX === 0 && e.clientY === 0;
        // openPopup(useTarget ? (e.target as HTMLElement) : e);
        e.preventDefault();
        e.stopPropagation();
        openPopup(e.target as HTMLElement);
      }}
      onKeyDown={(e) => {
        if ([" ", "ArrowDown"].includes(e.key)) {
          e.preventDefault();
          e.stopPropagation();
          openPopup(e.target as HTMLElement);
        } else if (["Enter"].includes(e.key)) {
          // try to click the submit button for them...
          const parent = getParent(
            e.target as HTMLElement,
            `.${styles.container}`
          );
          const button = parent?.querySelector(`.${styles.apply_button}`);
          (button as HTMLButtonElement)?.click();
        }
      }}
    >
      {props.children}
    </div>
  );
}
